"use client";

import { useState, useMemo, useEffect } from "react";
import CommonSchema from "@/components/common/CommonSchema";
import { motion } from "motion/react";
import {
  MapPin,
  Clock,
  Calendar,
  Compass,
  ArrowRight,
  Map,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Banner } from "../common/Banner";
import { FadeInSection } from "../common/FadeInSection";
import { cleanHeaderTags, createLookupObject, isValidImgSrc } from "@/_helper/helper";
import { useAppSelector } from "@/store/hooks";

export default function AttractionClient({
  attractionData,
  cmsData,
  initialBanner,
}: {
  attractionData: any[];
  cmsData: any;
  initialBanner?: any;
}) {
  const BannerList = useAppSelector((state) => state.home.BannerList);
  const [selectedCategory, setSelectedCategory] = useState<string>("All");
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, any>>({});

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  const banner: any =
    initialBanner ||
    BannerList?.find((banner: any) => banner?.BannerType === "attractions") ||
    {};

  const categories = useMemo(() => {
    const types = new Set(
      (attractionData || []).map((item: any) => item.AttractionCategory),
    );
    return ["All", ...Array.from(types).filter(Boolean)];
  }, [attractionData]);

  const filteredAttractions = useMemo(() => {
    return selectedCategory === "All"
      ? attractionData
      : attractionData.filter(
          (item: any) => item.AttractionCategory === selectedCategory,
        );
  }, [selectedCategory, attractionData]);

  return (
    <div className="min-h-screen bg-[#fdfcfa]">
      <CommonSchema />
      <Banner
        title={banner?.BannerTitle || "Local Attractions"}
        description={banner?.Description}
        bgImage={banner?.BannerImage}
        // icon={<Map className="w-12 h-12 text-[#d4af37]" />}
      />

      {/* Category Filter */}
      <section className="py-6 bg-white border-b border-[#5a6f3e]/10 sticky top-[75px] z-40">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-3">
            {categories.map((category: any) => (
              <button
                key={category}
                onClick={() => {
                  setSelectedCategory(category);
                  window.scrollTo({ top: 500, behavior: "smooth" });
                }}
                className={`px-8 py-3 transition-all cursor-pointer ${
                  selectedCategory === category
                    ? "bg-[#5a6f3e] text-white shadow-lg"
                    : "bg-[#f5f1ec] text-[#2a2520] hover:bg-[#e8dfd0]"
                }`}
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 500 }}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Attractions Grid */}
      <section className="py-24 bg-[#fdfcfa]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredAttractions.map((attraction: any, i: number) => (
              <FadeInSection key={attraction.ID} delay={i * 0.1}>
                <motion.div
                  whileHover={{
                    y: -10,
                    boxShadow: "0 25px 50px rgba(0,0,0,0.15)",
                  }}
                  transition={{ duration: 0.4 }}
                  className="bg-white group rounded-sm overflow-hidden flex flex-col h-full shadow-md"
                >
                  {/* Image Section */}
                  <div className="relative h-72 overflow-hidden">
                    <Image
                      src={
                        isValidImgSrc(attraction.MainImage)
                          ? attraction.MainImage
                          : "/imports/placeholder.png"
                      }
                      alt={attraction.Title}
                      fill
                      className="object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute top-4 left-4">
                      <span
                        className="bg-[#5a6f3e] text-white px-4 py-1.5 text-sm uppercase tracking-wider"
                        style={{ fontFamily: "Jost, sans-serif" }}
                      >
                        {attraction.AttractionCategory}
                      </span>
                    </div>
                  </div>

                  {/* Content Section */}
                  <div className="p-8 flex flex-col flex-grow">
                    <h3
                      className="text-2xl mb-2 text-[#2a2520]"
                      style={{
                        fontFamily: "Cormorant Garamond, serif",
                        fontWeight: 600,
                      }}
                    >
                      <Link href={`/attractions/${attraction.AttractionURL}`}>
                        {attraction.Title}
                      </Link>
                    </h3>
                    <p
                      className="text-[#5a6f3e] text-sm mb-6 italic"
                      style={{ fontFamily: "Jost, sans-serif" }}
                    >
                      {attraction.TagLine}
                    </p>

                    <div className="space-y-4 mb-8">
                      <div className="flex items-center gap-4 text-[#2a2520]/70">
                        <MapPin className="w-5 h-5 text-[#d4af37]" />
                        <span
                          className="text-sm"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Distance:{" "}
                          <span className="text-[#2a2520] font-medium">
                            {attraction.Distance}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-[#2a2520]/70">
                        <Clock className="w-5 h-5 text-[#d4af37]" />
                        <span
                          className="text-sm"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Travel Time:{" "}
                          <span className="text-[#2a2520] font-medium">
                            {attraction.TravelTime}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-[#2a2520]/70">
                        <Calendar className="w-5 h-5 text-[#d4af37]" />
                        <span
                          className="text-sm"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Best Time:{" "}
                          <span className="text-[#2a2520] font-medium">
                            {attraction.BestTime}
                          </span>
                        </span>
                      </div>
                    </div>

                    <div className="mt-auto pt-6 border-t border-[#f5f1ec]">
                      <Link
                        href={`/attractions/${attraction.AttractionURL}`}
                        className="flex items-center justify-between text-[#5a6f3e] group/btn"
                      >
                        <span
                          className="text-sm font-semibold tracking-widest uppercase"
                          style={{ fontFamily: "Jost, sans-serif" }}
                        >
                          Discover More
                        </span>
                        <div className="bg-[#f5f1ec] group-hover/btn:bg-[#5a6f3e] group-hover/btn:text-white p-2 transition-all">
                          <ArrowRight className="w-5 h-5" />
                        </div>
                      </Link>
                    </div>
                  </div>
                </motion.div>
              </FadeInSection>
            ))}
          </div>
        </div>
      </section>

      {/* Exploration Section (using CMS if available) */}
      {lookUpCMSData?.["Attractions - Section 1 : Bottom Content"] && (
        <section className="py-24 bg-[#f5f1ec]">
          <div className="max-w-4xl mx-auto px-6 text-center">
            <FadeInSection>
              <div
                dangerouslySetInnerHTML={{
                  __html: cleanHeaderTags(
                    lookUpCMSData?.["Attractions - Section 1 : Bottom Content"]
                      ?.Template,
                  ),
                }}
              />
            </FadeInSection>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-24 bg-gradient-to-br from-[#5a6f3e] to-[#4a5f2e] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <FadeInSection>
            <h2
              className="text-4xl md:text-5xl mb-6 leading-tight"
              style={{
                fontFamily: "Cormorant Garamond, serif",
                fontWeight: 400,
              }}
            >
              Plan Your{" "}
              <span style={{ fontWeight: 600, fontStyle: "italic" }}>
                Adventure
              </span>
            </h2>
            <p
              className="text-xl text-white/90 mb-12"
              style={{ fontFamily: "Jost, sans-serif", fontWeight: 300 }}
            >
              Our concierge team is ready to help you plan your excursions and
              discovery tours
            </p>

            <Link href="/contact">
              <motion.button
                whileHover={{
                  scale: 1.05,
                  boxShadow: "0 20px 40px rgba(212, 175, 55, 0.4)",
                }}
                whileTap={{ scale: 0.95 }}
                className="bg-[#d4af37] text-[#2a2520] px-14 py-5 text-lg shadow-2xl cursor-pointer"
                style={{ fontFamily: "Jost, sans-serif", fontWeight: 600 }}
              >
                Contact Concierge
              </motion.button>
            </Link>
          </FadeInSection>
        </div>
      </section>
    </div>
  );
}
